import { Stack } from '@mui/material';
import { isEmpty } from 'lodash';
import * as React from 'react';
import { memo, useCallback, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  FormArrayFields,
  FormSection,
  FormSwitch,
  FormTextField,
  FormTextFieldInlineEdit,
  List,
  ListItem,
} from 'components/index';
import { useFeatureAccess, useFormProvider, useGetUserLabels, useIsMobile, useProcessNewLabels } from 'hooks';
import { AccountPlanType } from 'enums';
import messages from 'messages';
import useUpdateNodeFormData from 'pages/app-pages/chatbot-builder-page/hooks/useUpdateNodeFormData';
import FormLabelsSelector from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/FormLabelsSelector';
import FormVariableField from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/FormVariableField';
import NodesFormTextEditor from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/NodesFormTextEditor';
import StepFormFieldsWrapper from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormFieldsWrapper';
import StepFormSubmitButtons from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormSubmitButtons';
import StepFormWrapper from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormWrapper';
import AnswersFormArrayField from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/options-question/AnswersFormArrayField';
import { OptionsQuestionStepFormFields } from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/options-question/types';
import {
  defaultOptionsQuestionStepFormValues,
  OptionsQuestionFormFieldsConfig,
  OptionsQuestionStepFormSchema,
} from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/options-question/utils';
import { Mention } from 'types';
import { characterLimitValue } from 'utils';

interface OptionsQuestionProps {
  id: string;
  defaultValues?: Partial<OptionsQuestionStepFormFields>;
  mentions?: Mention[];
  commands?: Mention[];
}
const OptionsQuestion = ({ defaultValues, id, mentions, commands }: OptionsQuestionProps) => {
  const [localDefaultValues, setLocalDefaultValues] = useState({
    ...defaultOptionsQuestionStepFormValues,
    ...defaultValues,
    nodeId: defaultValues?.nodeId || uuid(),
    labels: defaultValues?.labels || [],
    answers: isEmpty(defaultValues?.answers)
      ? [{ name: 'Option 1', option: '', nodeId: uuid(), labels: [], value: '' }]
      : defaultValues?.answers?.map((answer) => ({
          ...answer,
          labels: answer.labels || [],
          value: answer.value || '',
        })),
  });

  const onLocalDefaultValuesChange = useCallback(
    (newDefaultValues: Partial<OptionsQuestionStepFormFields>) => {
      setLocalDefaultValues((prev) => ({
        ...prev,
        ...newDefaultValues,
        answers:
          newDefaultValues.answers?.map((answer) => ({
            ...answer,
            labels: answer.labels || [],
            value: answer.value || '',
          })) || prev.answers,
      }));
    },
    [],
  );

  const formMethods = useFormProvider<OptionsQuestionStepFormFields>({
    schema: OptionsQuestionStepFormSchema,
    defaultValues: localDefaultValues,
  });
  const { name, labels, variable, input, answers, removeAnswerOptions, optionFullWidth, hideOptions, validationMessage } =
    OptionsQuestionFormFieldsConfig;

  const hasPremiumAccess = useFeatureAccess({ minPlan: AccountPlanType.Premium });

  const updateNodeFormData = useUpdateNodeFormData();
  const { labels: userLabels } = useGetUserLabels();

  const { isMobile } = useIsMobile();

  const localCommands = useRef(commands);
  const localMentions = useRef(mentions);

  const { reset, formState } = formMethods;
  const processNewLabels = useProcessNewLabels();

  const handleOnSubmit = async (formData: OptionsQuestionStepFormFields) => {
    const labelIds = await processNewLabels(formData.labels);

    // Process labels for each answer option
    const processedAnswers = await Promise.all(
      formData.answers.map(async (answer) => {
        if (answer.labels && answer.labels.length > 0) {
          // Process new labels that might be strings (label names) instead of IDs
          const processedLabelIds = await Promise.all(
            answer.labels.map(async (labelId) => {
              // Check if this is already an existing label ID
              const existingLabel = userLabels.find((ul) => ul.id === labelId);
              if (existingLabel) {
                return labelId; // Already an ID, return as is
              }

              // Check if this is a label name that needs to be converted to ID
              const labelByName = userLabels.find((ul) => ul.name === labelId);
              if (labelByName) {
                return labelByName.id; // Return the ID of the existing label
              }

              // This is a new label, create it
              const newLabelObjects = [{ value: labelId, label: labelId, color: '#default' }];
              const newLabelIds = await processNewLabels(newLabelObjects);
              return newLabelIds[0]; // Return the first (and only) new label ID
            }),
          );

          return {
            ...answer,
            labels: processedLabelIds,
          };
        }
        return answer;
      }),
    );

    await updateNodeFormData({
      id,
      formData: {
        ...formData,
        labels: labelIds,
        answers: processedAnswers,
      },
      oldVariable:
        formState.defaultValues?.variable !== formData.variable
          ? formState.defaultValues?.variable
          : undefined,
    });
  };

  return (
    <StepFormWrapper formMethods={formMethods} handleOnSubmit={handleOnSubmit}>
      <StepFormFieldsWrapper>
        <FormTextFieldInlineEdit {...name} variant='standard' size='small' />
        <FormLabelsSelector {...labels} onLocalDefaultValuesChange={onLocalDefaultValuesChange} />
        <Stack gap={0.5}>
          <Stack gap={2}>
            <NodesFormTextEditor
              characterLimit={characterLimitValue}
              autoFocus={!isMobile}
              {...input}
              mentions={localMentions.current}
              commands={localCommands.current}
            />
          </Stack>
          <FormVariableField {...variable} />
        </Stack>
        <FormSection title={answers.label}>
          <FormArrayFields
            name={answers.name}
            FormArrayFieldComponent={AnswersFormArrayField}
            addButtonText='Add Answer Item'
          />
        </FormSection>

        <List divider label='Aditional Options'>
          <ListItem>
            <FormSwitch
              {...removeAnswerOptions}
              infoIcon={{
                tooltip: messages.builder.removeAnswerOptionsTooltip,
                icon: 'info',
              }}
            />
          </ListItem>
          <ListItem>
            <FormSwitch {...optionFullWidth} />
          </ListItem>
          {hasPremiumAccess && (
            <ListItem>
              <FormSwitch
                {...hideOptions}
                infoIcon={{
                  tooltip: messages.builder.hideOptionsTooltip,
                  icon: 'info',
                }}
              />
            </ListItem>
          )}
        </List>
        <FormTextField {...validationMessage} size='small' />
      </StepFormFieldsWrapper>
      <StepFormSubmitButtons formState={formState} reset={reset} initiallyEnabled />
    </StepFormWrapper>
  );
};

export default memo(OptionsQuestion);
